import { z } from '@customerio/hydra-validation';
import type { AutoTLSDomain } from '@customerio/hydra-ui-api';
import { defineEntity } from '../shared/define-entity';

/**
 * Auto-TLS domain — a workspace-scoped custom domain used for SMS/WhatsApp
 * link shortening. Life cycle:
 *   drafted → dns_verified → gcp_certificate_created → gcp_authorizing → active
 * with two failure branches (`dns_verify_failed`, `gcp_authorizing_failed`, etc.).
 *
 * Backed by `/v1/environments/{env}/auto_tls_domains`. List / create / delete
 * on the collection, plus `.../{id}/verify_dns` for kicking off DNS
 * verification.
 *
 * State derivations live below as pure helpers rather than model methods so
 * they compose cleanly with React consumers and stay easy to unit-test.
 */

export type AutoTlsDomain = AutoTLSDomain;
export type AutoTlsDomainState = AutoTlsDomain['state'];
export type AutoTlsDomainPurpose = AutoTlsDomain['purpose'];
export type AutoTlsDnsRecord = AutoTlsDomain['config']['dns_records'][number];

export const AUTO_TLS_DOMAIN_STATES = {
  DRAFTED: 'drafted',
  DNS_VERIFY_FAILED: 'dns_verify_failed',
  DNS_VERIFIED: 'dns_verified',
  GCP_CERTIFICATE_CREATED: 'gcp_certificate_created',
  GCP_CERTIFICATE_CREATION_FAILED: 'gcp_certificate_creation_failed',
  GCP_CERTIFICATE_ADD_TO_MAP_FAILED: 'gcp_certificate_add_to_map_failed',
  GCP_AUTHORIZING: 'gcp_authorizing',
  GCP_AUTHORIZING_FAILED: 'gcp_authorizing_failed',
  ACTIVE: 'active',
} as const satisfies Record<string, AutoTlsDomainState>;

const AutoTlsDomainFromApi = z.looseObject({}).pipe(z.custom<AutoTlsDomain>());

export interface AutoTlsDomainListParams {
  purpose?: AutoTlsDomainPurpose;
}

/** @public */
export const autoTlsDomain = defineEntity<AutoTlsDomain, AutoTlsDomain, AutoTlsDomainListParams>({
  name: 'auto-tls-domain',
  scope: 'workspace',
  pk: (d) => String(d.id),
  schema: { read: AutoTlsDomainFromApi },
});

export const autoTlsDomainKeys = autoTlsDomain.keys;

export function parseAutoTlsDomain(raw: unknown): AutoTlsDomain {
  return autoTlsDomain.parse(raw);
}

/* -------------------------- derived state helpers ------------------------- */

/** DNS records haven't been verified yet — user still needs to complete setup. */
export function isVerifyDnsPending(d: AutoTlsDomain): boolean {
  return d.state === AUTO_TLS_DOMAIN_STATES.DRAFTED || d.state === AUTO_TLS_DOMAIN_STATES.DNS_VERIFY_FAILED;
}

/** Fully authorized and usable for link shortening. */
export function isActive(d: AutoTlsDomain): boolean {
  return d.state === AUTO_TLS_DOMAIN_STATES.ACTIVE;
}

/**
 * A pollable in-between state — DNS is verified, GCP is provisioning the
 * certificate. Callers use this to decide whether to enable background
 * refetching on the list.
 */
export function isProvisioning(d: AutoTlsDomain): boolean {
  return (
    d.state === AUTO_TLS_DOMAIN_STATES.DNS_VERIFIED ||
    d.state === AUTO_TLS_DOMAIN_STATES.GCP_CERTIFICATE_CREATED ||
    d.state === AUTO_TLS_DOMAIN_STATES.GCP_AUTHORIZING
  );
}

/**
 * Provisioning failed somewhere in the GCP pipeline — certificate creation, map
 * registration, or authorization. A terminal error state that supersedes the
 * domain's own link-tracking verification flags wherever the two are shown
 * together.
 */
export function hasGcpCertificateError(d: AutoTlsDomain): boolean {
  return (
    d.state === AUTO_TLS_DOMAIN_STATES.GCP_CERTIFICATE_CREATION_FAILED ||
    d.state === AUTO_TLS_DOMAIN_STATES.GCP_CERTIFICATE_ADD_TO_MAP_FAILED ||
    d.state === AUTO_TLS_DOMAIN_STATES.GCP_AUTHORIZING_FAILED
  );
}

/** Certificate creation step of the provisioning pipeline. */
export function gcpCertificateCreationStates(d: AutoTlsDomain) {
  return {
    isCompleted:
      d.state === AUTO_TLS_DOMAIN_STATES.GCP_CERTIFICATE_CREATED ||
      d.state === AUTO_TLS_DOMAIN_STATES.GCP_AUTHORIZING ||
      d.state === AUTO_TLS_DOMAIN_STATES.GCP_AUTHORIZING_FAILED ||
      d.state === AUTO_TLS_DOMAIN_STATES.ACTIVE,
    // Once DNS is verified, cert creation automatically starts.
    isInProgress: d.state === AUTO_TLS_DOMAIN_STATES.DNS_VERIFIED,
    isFailed:
      d.state === AUTO_TLS_DOMAIN_STATES.GCP_CERTIFICATE_CREATION_FAILED ||
      d.state === AUTO_TLS_DOMAIN_STATES.GCP_CERTIFICATE_ADD_TO_MAP_FAILED,
  };
}

/** Certificate authorization step of the provisioning pipeline. */
export function gcpAuthorizingStates(d: AutoTlsDomain) {
  return {
    isCompleted: d.state === AUTO_TLS_DOMAIN_STATES.ACTIVE,
    isInProgress:
      d.state === AUTO_TLS_DOMAIN_STATES.GCP_AUTHORIZING || d.state === AUTO_TLS_DOMAIN_STATES.GCP_CERTIFICATE_CREATED,
    isFailed: d.state === AUTO_TLS_DOMAIN_STATES.GCP_AUTHORIZING_FAILED,
  };
}
